/* PLEASE NOTE:
 * This file is used to store all the constants used in the application.
 * For API endpoints, please check the `../api/endpoints.ts` file.
 */

import { TSelectboxOption } from "../components/admin/formFields";
import { ColorRGB, ReportOptionsDropdownConfig } from "../types/common";
import { localStorageKeys } from "../utils/localStorageItems";
import { ReportOptionsAdditionalDropdown } from "./type";

export enum REPORT_FILTER_BY_OPTION_VALUES {
  GROUP = "1",
  ASSET = "2",
}
export enum REPORT_FILTER_BY_OPTION_FIELD_NAME {
  GROUP = "device_groups",
  ASSET = "device_id",
}
export enum REPORT_ADVANCE_FILTER_OPTIONS {
  COLUMN = "column",
  OPERATOR = "operator",
  VALUE = "value",
}

const ALL_DEBUGGING_ENABLED = ["true", "1"].includes(
  localStorage.getItem(localStorageKeys.DISABLE_ALL_DEBUGGING) || ""
)
  ? false
  : true;

// currently SSO is being released on production in test/debug mode,
// thus, general users are not expected to make use of it.
// once the SSO feature goes live, this constant can be replaced with a simple boolean 'true' value, and the related localStorage item and checks can be removed.
export const SSO_ENABLED = ["1", "true"].includes(
  localStorage.getItem(localStorageKeys.TEMP_SSO_ENABLED) ?? "false"
);

export enum GOOGLE_MAP_MODE_NAMES {
  ROADMAP = "roadmap",
  SATELLITE = "satellite",
  BIRDSEYE = "birdseye",
  STREETVIEW = "streetview",
}

export const ASSET_LABEL_COMPONENT_VALUES = {
  DEVICEID: "deviceid",
  DESCRIPTION: "description",
  DISPLAYNAME: "displayname",
  DRIVERNAME: "drivername",
};

// this search field allows various admin entities to be searched by driver name.
// however, this field gets excluded from search fields list if the account does not have drivers enabled,
// as determined by the 'hasdrivers' value in the account settings (logged in user's data).
export const DRIVER_NAME_SEARCH_FIELD = "drivername";

export const APP_CONFIG = {
  APP_NAME: "FleetTrack",
  API_POLLING_INTERVALS: {
    NOTIFICATION_SUMMARY:
      (window.location.hostname.includes("localhost")
        ? 60 // 1 minute for localhost
        : 20) * // 20 seconds for production
      1000,
    UPLOADS_DOWNLOADS: 10 * 1000, // 10 seconds
    VISIONMAX_VOICECALL_INCOMING: 5 * 1000, // 30 seconds
  },
  BUZZER_ALERTS: {
    IGNORED_BUZZER_ALERTS_CHECK: 10 * 1000, // deprecated // 10 seconds
    IGNORED_PENDING_MINUTES: 15,
    IGNORED_IN_PROGRESS_MINUTES: 30,
  },
  CHAT: {
    PUBNUB_MAX_UPLOAD_SIZE_MB: 5,
  },
  COLORS: {
    // should stay consistent with tailwind.config.js
    GREEN: "#009007",
    RED: "#D0021B",
  },
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 20,
    SPECIAL_CHARACTERS: "~`!@#$%^&*()_-+={[}]|\\:;\"'<,>.?/",
  },
  REGEX: {
    ALPHANUMERIC: /^[a-zA-Z0-9 ]*$/,
    VIN: /^[A-HJ-NPR-Z0-9]{17}$/,
    NUMERIC: /^[0-9]*$/,
    YEAR: /^[0-9]{4}$/,
    LICENSE_PLATE: /^[a-zA-Z0-9 ]*$/,
    PHONE_NUMBER: /^[0-9]{10}$/,
  },
  DEFAULT_TIMEZONE: "US/Central",
  LISTINGS: {
    PAGE_SIZES: [10, 20, 50, 100],
    DEFAULT_PAGE_SIZE: 10,
    LHS_RHS_PAGE_SIZE: 50,
    VEHICLE_PAGE_SIZE: 200, // 50,
    DROPDOWN_SEARCH_PAGE_SIZE: 20,
    LARGE_PAGE_SIZE: 100, // for groups
    TEMP_LARGE_PAGE_SIZE: 100, // TODO: remove wherever used and implement proper pagination / infinite loading / load more
    XL_PAGE_SIZE: 500,
    GROUP_HIERARCHY_PAGE_SIZE: 50,
  },
  ENTITY_SEARCH_FIELDS: {
    ALL_FLAG: "*", // used internally (FE) to ensure all database fields are searched

    // buzzer / panic alerts
    BUZZER_ALERT_LISTING: [
      "rule_description",
      "deviceid",
      "device_description",
      "displayname",
      DRIVER_NAME_SEARCH_FIELD,
      "event_info__address", // [TBD] needs to be added in the backend as well for it to work
    ],

    CAMERA_LISTING: [
      "serial_number",
      // -------------------------
      // asset related fields (below) are intentionally removed, as the dynamic asset search fields
      // configured at account level are included in the API definition
      // -------------------------
      // "ft_device_id",
      // "ft_device_description",
      // "ft_displayname",
      // DRIVER_NAME_SEARCH_FIELD,
    ],

    // crash track / crash reconstruction
    CRASH_TRACK_LISTING: [
      "deviceid",
      "description",
      "displayname",
      DRIVER_NAME_SEARCH_FIELD,
      "vehiclemake",
      "vehiclemodel",
      "vehiclevin",
      "groups",
      "imeinumber",
    ],

    // devices / assets (below)

    // the 'DEVICE_LISTING' fields (below) are used as default search fields in asset listing/search APIs
    // when the account level asset_searchable_fields is not defined or is empty.
    // These fields are also used in the search dropdown in the UI, but can be overridden by account level asset_searchable_fields.
    DEVICE_LISTING: [
      "deviceid",
      "description",
      "displayname",
      DRIVER_NAME_SEARCH_FIELD,
      "driverid",
      "uniqueid",
      "vehicleid", // Asset VIN
      "devicecode",
      "cameraid",
    ], // added description per client request

    // the 'DEVICE_SEARCH' (below) is deprecated and should not be used in new code.
    // Instead the account-level asset_searchable_fields value is used in all asset dropdowns.
    // DEVICE_SEARCH: ["deviceid", "displayname", "description", "drivername"],

    // device drivers - special case
    DEVICE_DRIVER_SEARCH: ["driverid", "drivername", "description"], // special case when drivers are fetched from device API

    // documents
    DOCUMENT_LISTING: ["name", "file_name"],

    // drivers
    DRIVER_LISTING: [
      "driverid",
      "description",
      "badgeid",
      "cardid",
      "licensenumber",
      "licensetype",
      // "licenseexpire", // [TBD] adding 'licenseexpire' involves implementation challenges (conversion)
    ],
    DRIVER_DROPDOWN: ["driverid", "description", "drivername"],

    // fleettags
    FLEETTAG_LISTING: [
      "entityid",
      "description",
      "address",
      "latitude",
      "longitude",
      "deviceid",
      "device_description",
      "device_displayname",
      DRIVER_NAME_SEARCH_FIELD,
    ],

    // geozones
    GEOZONE_LISTING: [
      "geozoneid",
      "description",
      // "zonetype", // [TBD] adding 'zonetype' involves implementation challenges (conversion)
      // <shape details> // [TBD] adding shape details involves implementation challenges (conversion)
    ],
    GEOZONE_DROPDOWN: ["geozoneid","description"],

    // geozone categories
    GEOZONE_CATEGORY_LISTING: ["categoryid", "displayname", "description"],

    // groups
    GROUP_LISTING: ["groupid", "description"],
    GROUP_DROPDOWN: ["groupid","description"],

    // maintenance
    // 'MAINTENANCE_LISTING' is deprecated,
    // instead the the dynamic asset search fields configured at account level
    // are used for maintenance listing/search APIs as well
    MAINTENANCE_LISTING: [
      // "deviceid",
      // "description",
      // "displayname",
      // "devicecode",
      // "driverid",
      // DRIVER_NAME_SEARCH_FIELD,
      // -------------------------
      // asset related fields (above) are intentionally removed, as the dynamic asset search fields
      // configured at account level are included in the API definition
      // -------------------------
      "uniqueid",
      "equipmenttype",
      "cameraid",
    ],

    // roles
    ROLE_LISTING: ["roleid", "description"],
    ROLE_DROPDOWN: ["roleid"],

    // rules
    RULE_LISTING: [
      "ruleid",
      "description",
      "selector",
      // "ruletag", // [TBD] adding 'ruletag' involves implementation challenges
    ],

    // users
    USER_LISTING: [
      "userid",
      "description",
      "roleid",
      "contactname",
      "contactemail",
    ],
    USER_DROPDOWN: ["userid", "contactname"],

    // vehicle alerts
    VEHICLE_ALERT_LISTING: [
      // "deviceid",
      // "ft_description",
      // "displayname",
      // DRIVER_NAME_SEARCH_FIELD,
      // -------------------------
      // asset related fields (above) are intentionally removed, as the dynamic asset search fields
      // configured at account level are included in the API definition
      // -------------------------
      "rule.description", // deeper level field, supported via dot notation in the backend
      // 'rule.ruleid', // available in backed but currently not in use on FE
      // 'rule.lastupdatetime', // available in backed but currently not in use on FE
      // 'rule.creationtime', // available in backed but currently not in use on FE
      "rule.ruletag", // deeper level field, supported via dot notation in the backend
    ],

    GENERIC_STATUS_CODE_SEARCH: ["statuscode", "statusname", "description"],
  },
  ASSET_CONFIGS: {
    LABEL_COMPONENTS: [
      { label: "Asset ID", value: ASSET_LABEL_COMPONENT_VALUES.DEVICEID },
      {
        label: "Asset Description",
        value: ASSET_LABEL_COMPONENT_VALUES.DESCRIPTION,
      },
      {
        label: "Asset Display Name",
        value: ASSET_LABEL_COMPONENT_VALUES.DISPLAYNAME,
      },
      { label: "Driver Name", value: ASSET_LABEL_COMPONENT_VALUES.DRIVERNAME },
    ],
    DEFAULT_LABEL_CONFIG: [
      ASSET_LABEL_COMPONENT_VALUES.DEVICEID,
      ASSET_LABEL_COMPONENT_VALUES.DESCRIPTION,
      ASSET_LABEL_COMPONENT_VALUES.DISPLAYNAME,
    ],
    SEARCHABLE_FIELDS: [
      { label: "Asset ID", value: ASSET_LABEL_COMPONENT_VALUES.DEVICEID },
      { label: "Unique ID", value: "uniqueid" },
      { label: "Asset VIN", value: "vehicleid" }, // Asset VIN
      {
        label: "Asset Display Name",
        value: ASSET_LABEL_COMPONENT_VALUES.DISPLAYNAME,
      },
      // { label: "Server ID", value: "serverid" }, // disabled as "Server ID" is actually "devicecode" in the backend
      { label: "Server Id", value: "devicecode" },
      {
        label: "Asset Description",
        value: ASSET_LABEL_COMPONENT_VALUES.DESCRIPTION,
      },
      { label: "Driver Name", value: ASSET_LABEL_COMPONENT_VALUES.DRIVERNAME },
      { label: "Driver ID", value: "driverid" },
    ],
    MANDATORY_SEARCH_FIELDS: ["vehiclemake", "vehiclemodel", "vehicleyear"],
  },
  DOCUMENT_UPLOAD: {
    ALLOWED_IMAGE_FILE_EXT: ["jpg", "jpeg", "png"],
    ALLOWED_FILE_EXT: ["pdf", "jpg", "jpeg", "png"],
    MAX_FILE_SIZE_MB: 5, // used as fallback, overridden by user's config received from Login API
  },
  TOASTS: {
    INFO: false,
  },
  DES: {
    // design
    DASH: {
      P_HORIZ: "px-6",
    },
  },
  DEBUG: {
    ALL:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.ALL")),
    VERBOSE: ALL_DEBUGGING_ENABLED && false,
    BUZZER:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.BUZZER")),
    MAPS:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.MAPS")),
    MAPS_GSNAPPOINTSTOROAD:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.MAPS_GSNAPPOINTSTOROAD")),
    MAPS_GDIRECTIONSSERVICE:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.MAPS_GDIRECTIONSSERVICE")),
    MAPS_TRIPEVALUATION:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.MAPS_TRIPEVALUATION")),
    // ALL_DEBUGGING_ENABLED &&
    // (window.location.hostname.includes("localhost") ||
    //   !!localStorage.getItem("DEBUG.MAPS_GSNAPPOINTSTOROAD")),
    MAP_MARKERS:
      ALL_DEBUGGING_ENABLED &&
      false &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.MAP_MARKERS")),
    GEOZONES:
      ALL_DEBUGGING_ENABLED &&
      false &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.GEOZONES")),
    REPORTS:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.REPORTS")),
    DISPLAY_VERBOSE:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.DISPLAY_VERBOSE")),
    MESSAGING:
      ALL_DEBUGGING_ENABLED &&
      (window.location.hostname.includes("localhost") ||
        !!localStorage.getItem("DEBUG.MESSAGING")),
    PUBNUB:
      ALL_DEBUGGING_ENABLED &&
      // window.location.hostname.includes("localhost") ||
      !!localStorage.getItem("DEBUG.PUBNUB"),
    PUBNUB_VERBOSE:
      ALL_DEBUGGING_ENABLED && !!localStorage.getItem("DEBUG.PUBNUB_VERBOSE"),
  },
  DEBOUNCE: {
    SEARCH: 500,
  },
  TOGGLE_FT_ALLOWED: {
    YES: "1",
    NO: "0",
  },
  MAPS: {
    SCRIPT_ID: "bing-maps-script",
    SCRIPT_URL: (key: string) =>
      `https://maps.googleapis.com/maps/api/js?key=${key}&loading=async` +
      `&libraries=maps,places` + // geometry
      `&v=beta`, // callback=initMap&setLang=en&setMkt=en-US
    COMPONENT_ID: "FTGoogleMap", // FTBingMap
    TYPES_INDEX: {
      // ROAD: 0,
      // AERIAL: 1,
      // ORDNANCE_SURVEY: 2,
      // BIRDSEYE: 3,
      // STREETSIDE: 4,
      // THREED: 5,
      ROADMAP: 0,
      SATELLITE: 1,
      BIRDSEYE: 2,
      STREETVIEW: 3,
    },
    TYPES: [
      // { type: "road", id: "r" },
      // { type: "aerial", id: "a" },
      // { type: "ordnanceSurvey", id: "os" },
      // { type: "birdseye", id: "be" },
      // { type: "streetside", id: "x" },
      // { type: "3d", id: false },
      { type: GOOGLE_MAP_MODE_NAMES.ROADMAP, id: "r" },
      { type: GOOGLE_MAP_MODE_NAMES.SATELLITE, id: "a" },
      { type: GOOGLE_MAP_MODE_NAMES.BIRDSEYE, id: "b" },
      { type: GOOGLE_MAP_MODE_NAMES.STREETVIEW, id: "s" },
    ],
    SPECIAL_VIEW_TYPES: [
      GOOGLE_MAP_MODE_NAMES.BIRDSEYE,
      GOOGLE_MAP_MODE_NAMES.STREETVIEW,
    ],
    VIEW_TYPES_REQUIRING_METADATA_CHECK: [GOOGLE_MAP_MODE_NAMES.STREETVIEW],
    STREETSIDE_MAP_MODE: 2, // map.getMode().mapModeType
    ZOOM_MIN: 3,
    ZOOM_MAX: 19, // initially client has requested to limit map zoom to 18 as max value, later in Oct 2025 (now) this value is being changed to 19 to allow display of house numbers on the map
    DEFAULTS: {
      TYPE: GOOGLE_MAP_MODE_NAMES.ROADMAP,
      CENTER: {
        // Memphis, TN (LB Technology HQ)
        // latitude: 35.129186,
        // longitude: -89.9571074,
        // center coordinates for United States
        latitude: 39.828127,
        longitude: -98.579404,
      },
      ZOOM: 3, // 7, changed from 7 to 3 because when there's nothing to focus on, the client wants to see world map
      EVENT_ZOOM: 18,
      RADIUS: 1000, // in meters
      RADIUS_CORRIDOR: 1000, // in meters
      POLYGON_POINTS_RANGE: [3, 10],
      POLYGON_POINTS: 6,
      ROUTE_POINTS_MAX: 10, // same as POLYGON_POINTS_RANGE[1] as the DB supports max 10 points
      ZONE_COLOR: [0, 150, 100] as ColorRGB,
    },
    COLORS: {
      Default: [0, 150, 100] as ColorRGB,
      Black: [0, 0, 0] as ColorRGB,
      Brown: [139, 69, 19] as ColorRGB,
      Red: [200, 0, 0] as ColorRGB,
      Orange: [200, 100, 0] as ColorRGB,
      Yellow: [200, 200, 0] as ColorRGB,
      Green: [0, 200, 0] as ColorRGB,
      Blue: [0, 0, 200] as ColorRGB,
      Purple: [100, 0, 200] as ColorRGB,
      Gray: [100, 100, 100] as ColorRGB,
      White: [255, 255, 255] as ColorRGB,
      Cyan: [0, 200, 200] as ColorRGB,
      Pink: [200, 0, 200] as ColorRGB,
    },
    VEHICLE_ICON_SIZE: 36, // initially 28
    // MAP_ICON_MULTIPLIER: 1.5, // deprecated, replaced with getAccountMapIconSizeMultiplierNoHook() function
    VEHICLE_IGNITION_STATE_LABELS: {
      // used in vehicle details popup
      "2": "ON",
      "1": "OFF",
    },
    VEHICLE_EVENT_ICON_SIZE: 28,
    VEHICLE_EVENT_ICON_ONLY_SIZE: 15,
    VEHICLE_EVENT_STARTEND_ICON_SIZE: 40,
    SHOW_INFOBOX_PUSHPIN_THRESHOLD: 6,
    INFOBOX_ZINDEX: 100,
    DIRECTIONS_PANEL: 50,
    REFRESH_BUTTONS_ZINDEX: 50,
    ROUTE_STYLE_FILTER_BUTTON_ZINDEX: 60,
    CONTROL_BUTTONS_ZINDEX: 200, // set explicitly to 200 in `index.scss`
    DB_GEOZONE_RADIUS_UNIT_FOR_MAPS: () => {
      const Microsoft = (window as any).Microsoft;
      return Microsoft.Maps.SpatialMath.DistanceUnits.Meters; // meters
    },
    TRIPS_SHOW_DOT_WITH_START_STOP_MARKERS: false,
    USE_SNAP_TO_ROAD_FOR_TRIP: true,
    ASSET_MAP: {
      ROUTE_DISPLAY: {
        METHODS: {
          POINT_TO_POINT: "POINT_TO_POINT",
          SNAP_TO_ROAD: "SNAP_TO_ROAD",
          DIRECTIONS_SERVICE: "DIRECTIONS_SERVICE",
        },
        DEFAULT: "POINT_TO_POINT",
      },
    },
    // `ASSET_MAP_SHOW_SINGLE_MARKER_FOR_EVENT_CODES` deprecated
    // client does not want the feature to be bound with status codes
    // ASSET_MAP_SHOW_SINGLE_MARKER_FOR_EVENT_CODES: {
    //   stopped: [
    //     VehicleStatusCode.STATUS_MOTION_STOP,
    //     VehicleStatusCode.STATUS_MOTION_STOPPED,
    //   ] as VehicleStatusCode[],
    // } satisfies { [key: string]: VehicleStatusCode[] },
    REFRESH_FREQUENCY_OPTIONS: [
      ...(window.location.hostname.includes("localhost")
        ? [{ label: "15 sec", value: "15" }]
        : []),
      { label: "30 sec", value: "30" },
      { label: "1 min", value: "60" },
      { label: "2 min", value: "120" },
    ] as TSelectboxOption[],
  },
  LAT_LNG_LINK: (lat: number, lng: number) =>
    `https://www.bing.com/maps/?cp=${lat}%7E${lng}&lvl=18.0`, // `https://www.google.com/maps/search/${lat},${lng}`,
  COUNTRY_CODE:
    localStorage.getItem(localStorageKeys.TEMP_PHONE_COUNTRY_CODE) ?? "1", // default to US

  CAMERA_ADMIN_URL: `https://video.lbtelematics.com/#/login`,
  COMPLIANCE_ADMIN_URL: `https://fleettrackhos.lbtelematics.net/`,
  DROPDOWN_ZINDEX: 5,

  CAMERA_ADMIN: {
    LIVEVIEW_RETRIES: 3,
  },

  DATETIME_FORMATS: {
    // formats used for datepicker
    DATEPICKER: {
      // datepicker field formats
      DATE_ONLY: "MM/dd/yyyy",
      DATE_TIME_AP: "MM/dd/yyyy hh:mm aa",
      DATE_TIME_SEC_AP: "MM/dd/yyyy h:mm:ss aa",
      TIME_ONLY: "h:mm:ss aa",
      // datepicker output formats
      OUTPUT_DATE_TIME: "yyyy-MM-dd'T'HH:mm",
      OUTPUT_DATE_TIME_SEC: "yyyy-MM-dd'T'HH:mm:ss",
    },
    // formats used for display
    DISPLAY: {
      DATE_TIME: "MM/dd/yyyy h:mm:ss aa",
      DATE_ONLY: "MM/dd/yyyy",
      TIME_ONLY: "h:mm:ss aa",
      TIME_24_H: "HH:MM:SS",
    },
    BACKEND: {
      DATE: "yyyy-MM-dd",
      TIME: "HH:mm:ss",
    },
  },

  HEATMAP: {
    TIME_DURATION_HOURS: 12,
  },

  HELP: {
    HELPDOCS_USER_MANUAL_TITLE: "User Manual",
    USER_MANUAL_ENABLED: true,
  },

  REPORTS: {
    BASIC_DROPDOWN_FILTERS: {
      DEVICE: {
        field_name: "device_id",
        active_status_toggle: true,
        faux_option_all: true, // faux_option_all is used to add an "All" option
        multiple: true,
      } as ReportOptionsDropdownConfig,
      DRIVER: {
        field_name: "driver_id",
        multiple: true,
      } as ReportOptionsDropdownConfig,
      GEOZONE: { field_name: "geozone_id" } as ReportOptionsDropdownConfig,
      GROUP: {
        field_name: "device_groups",
        faux_option_all: true, // faux_option_all is used to add an "All" option
        multiple: true,
        active_status_toggle: true,
      } as ReportOptionsDropdownConfig,
    },
    GROUP_ASSETS_FILTER: {
      field_name: "device_groups_isactive", // reportFilters value (object) received from APIs as "0" or "1"
    },
    DROPDOWN_ALL_OPTION_VALUE: "ALL",
    TIME_RANGE_OPTIONS: [
      // labels below are overwritten by their translations, keys in comments below are used
      { id: "PREVIOUS_DAY", label: "Previous Day", value: "1" }, // reports.label_time_range_1
      { id: "LAST_7_DAYS", label: "Last 7 Days", value: "2" }, // reports.label_time_range_2
      { id: "PREVIOUS_WEEK", label: "Previous Week", value: "3" }, // reports.label_time_range_3
      { id: "LAST_30_DAYS", label: "Last 30 Days", value: "4" }, // reports.label_time_range_4
      { id: "PREVIOUS_MONTH", label: "Previous Month", value: "5" }, // reports.label_time_range_5
    ],
    FILTER_BY_GROUP_ASSET_OPTIONS: [
      // labels below are overwritten by their translations, keys in comments below are used
      {
        id: "GROUPS",
        label: "Groups",
        value: REPORT_FILTER_BY_OPTION_VALUES.GROUP,
      }, // reports.label_filter_by_group_asset_1
      {
        id: "ASSETS",
        label: "Assets",
        value: REPORT_FILTER_BY_OPTION_VALUES.ASSET,
      }, // reports.label_filter_by_group_asset_2
    ],
    GEOZONE_LOOKUP_LATITUDE_LONGITUDE: [
      { field_name: "geozone_lookup_add_lat" },
      { field_name: "geozone_lookup_add_long" },
    ],
    GROUP_ASSET_ALL_VALUE: "*",
    GROUP_ASSET_ALL_ACTIVE_VALUE: "@", // special value for backend to represent "Only Active" assets or groups
    ADDITIONAL_DROPDOWN_VALUES: [
      {
        field_name: "alert_status",
        options: [
          { label: "All Alerts", value: "4" },
          { label: "Tamper", value: "1" },
          { label: "Panic", value: "2" },
          { label: "Tow", value: "3" },
        ],
      },
      {
        field_name: "asset_location_flag",
        options: [
          { label: "Ignition On", value: "1" },
          { label: "Moving", value: "2" },
        ],
      },
      {
        field_name: "digital_input",
        options: [
          { label: "Input #1", value: "1" },
          { label: "Input #2", value: "2" },
          { label: "Input #3", value: "3" },
          { label: "Input #4", value: "4" },
          { label: "Input #5", value: "5" },
          { label: "Input #6", value: "6" },
          { label: "Input #7", value: "7" },
        ],
      },
      {
        field_name: "distance",
        options: [
          { label: "Within 500-feet", value: "1" },
          { label: "Within 1-mile", value: "2" },
          { label: "Within 2-miles", value: "3" },
          { label: "Within 3-miles", value: "4" },
          { label: "Within 5-miles", value: "5" },
          { label: "Within 10-miles", value: "6" },
          { label: "Within 20-miles", value: "7" },
        ],
      },
      {
        field_name: "exceeding_speed",
        options: [
          { label: "over limit", value: "1" },
          { label: "5mph over limit", value: "2" },
          { label: "6mph over limit", value: "3" },
          { label: "10mph over limit", value: "4" },
          { label: "15mph over limit", value: "5" },
        ],
      },
      {
        field_name: "expire_license_flag",
        options: [
          { label: "Expired Now", value: "1" },
          { label: "Within 1 week", value: "2" },
          { label: "Within 2 weeks", value: "3" },
          { label: "Within 1 month", value: "4" },
          { label: "Within 6 months", value: "5" },
        ],
      },
      {
        field_name: "fleet_ideal_flag",
        options: [
          { label: "All", value: "" },
          { label: "Input #2 (ON)", value: "1" },
          { label: "Input #2 (OFF)", value: "2" },
        ],
        optional: true,
      },
      {
        field_name: "geozone_lookup_distance",
        options: [
          { label: "Within 500 feet", value: "1" },
          { label: "Within 1000 feet", value: "2" },
          { label: "Within 2000 feet", value: "3" },
          { label: "Within 3000 feet", value: "4" },
          { label: "Within 4000 feet", value: "5" },
          { label: "Within 5000 feet", value: "6" },
        ],
      },
      {
        field_name: "ignition_status",
        options: [
          { label: "Based on Ignition On/Off Status Codes", value: "1" },
          { label: "Based on Motion Start/Stop Status Codes", value: "2" },
          { label: "Based on Engine Start/Stop Status Codes", value: "3" },
          { label: "Based on 0 Minute Minimum Stop", value: "4" },
          { label: "Based on 1 Minute Minimum Stop", value: "5" },
          { label: "Based on 2 Minute Minimum Stop", value: "6" },
          { label: "Based on 3 Minute Minimum Stop", value: "7" },
          { label: "Based on 5 Minute Minimum Stop", value: "8" },
          { label: "Based on 10 Minute Minimum Stop", value: "9" },
          { label: "Based on 20 Minute Minimum Stop", value: "10" },
          { label: "Based on 30 Minute Minimum Stop", value: "11" },
        ],
      },
      {
        field_name: "last_asset_location",
        options: [
          { label: "All Events", value: "" },
          { label: "'Waymark_0' Events", value: "1" },
        ],
        optional: true,
      },
      {
        field_name: "last_check_in",
        options: [
          { label: "More than 1 days", value: "1" },
          { label: "More than 2 day", value: "2" },
          { label: "More than 3 days", value: "3" },
          { label: "More than 7 days", value: "4" },
          { label: "More than 10 days", value: "5" },
          { label: "More than 30 days", value: "6" },
          { label: "More than 100 days", value: "7" },
        ],
      },
      {
        field_name: "last_ignition",
        options: [
          { label: "More than 0 days", value: "1" },
          { label: "More than 1 day", value: "2" },
          { label: "More than 2 days", value: "3" },
          { label: "More than 3 days", value: "4" },
          { label: "More than 7 days", value: "5" },
          { label: "More than 30 days", value: "6" },
          { label: "More than 100 days", value: "7" },
        ],
      },
      {
        field_name: "speed_over",
        options: [
          { value: "45", label: "45 mph" },
          { value: "50", label: "50 mph" },
          { value: "55", label: "55 mph" },
          { value: "60", label: "60 mph" },
          { value: "65", label: "65 mph" },
          { value: "70", label: "70 mph" },
          { value: "75", label: "75 mph" },
        ],
      },
      {
        field_name: "speeding_events",
        options: [
          { label: "Default", value: "1" },
          { label: "Status Code", value: "2" },
        ],
      },
      {
        field_name: "gear_selector_flag",
        options: [
          { label: "ALL", value: "" },
          { label: "PARK", value: "1" },
          { label: "REVERSE", value: "2" },
          { label: "NEUTRAL", value: "3" },
          { label: "DRIVE", value: "4" },
        ],
        optional: true,
      },
      {
        field_name: "speed_more_than_flag",
        options: [
          { label: "Stopped more than 0 minutes", value: "1" },
          { label: "Stopped more than 5 minutes", value: "2" },
          { label: "Stopped more than 10 minutes", value: "3" },
          { label: "Stopped more than 20 minutes", value: "4" },
          { label: "Stopped more than 30 minutes", value: "5" },
        ],
      },
      {
        field_name: "delinquent_flag",
        options: [
          { label: "All Delinquent", value: "" },
          { label: "True Delinquent Only", value: "1" },
        ],
        optional: true,
      },
    ] as ReportOptionsAdditionalDropdown[],
    MAX_COLS_FOR_PDF_DOWNLOAD: 6,
    MAX_COLS_FOR_PDF_SHARE: 6,
    NUMBER_CELLDATA_ALIGNMENT: "left" as "left" | "right" | "center",
  },
  PRIMARY_SYSADMIN_ACCOUNT: "sysadmin", // LB Technology primary account
  SYSADMIN_SUPERADMIN_ACCOUNTS: ["sysadmin"], // users under 'sysadmin' accounts
  SYSADMIN_SUPERADMIN_USER_CAN_ACCESS_ALL_APP_FEATURES: true,

  CRASHTRACK: {
    HIGHLIGHT_EVENT_ON_INCIDENT_MAP: true,
    USE_SPL_ICON: false, // client does not want to use any special icon
  },

  VISIONMAX: {
    REQUEST_VIDEO: {
      DEFAULT_CLIP_DURATION_SEC: 30,
    },
  },

  AZURE_AD: {
    AUTHORITY_PREFIX: "https://login.microsoftonline.com/",
    REDIRECT_URI: "/auth/azure/callback", // used to handle azure token after azure redirects back to react app
    LOGOUT: "/auth/azure/logout-callback", // used to complete logout in react app after azure logout completes
  },
};

export enum REPORT_TYPES {
  STANDARD = "1",
  CUSTOM = "2",
  SHARED_WITH_ME = "3",
  // SHARED_WITH_OTHERS = '4',
  NEW_CUSTOM = "5",
}

export enum REPORT_CATEGORIES {
  ASSET_DETAIL = "1",
  DEVICE_GROUP_DETAIL = "2",
  DEVICE_GROUP_SUMMARY = "3",
  ASSET_PERFORMANCE = "4",
  DRIVER_PERFORMANCE = "5",
  IFTA = "6",
  OTHERS_DASHBOARD_WIDGETS = "7",
  // Value "8" (formerly IFTA_GROUP) was merged into IFTA ("6"); see backend report migration 0056.

  NEW_CUSTOM_ASSET = "9",
  NEW_CUSTOM_DRIVER = "10",
  NEW_CUSTOM_USER = "11",

  INPUT_REPORTS = "12",
  SNOW_PLOW_REPORTS = "13",
  MAINTENANCE_REPORTS = "14",
  FUEL_CARD_INTEGRATION = "15",
  FLEET_TAG_REPORTS = "16",
  DRIVER_REPORTS = "17",
}

// backend uses aliases like US/Central, US/Eastern, etc. for timezones
// source: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
// source: https://github.com/nodatime/nodatime/issues/1342
// source: https://stackoverflow.com/a/59923205/1268350
// source: https://github.com/eggert/tz/blob/main/backward

export const TIMEZONE_OPTIONS = [
  { id: "US_HAWAII", value: "US/Hawaii", label: "US/Hawaii" }, // canonical: "Pacific/Honolulu"
  { id: "US_ALASKA", value: "US/Alaska", label: "US/Alaska" }, // canonical: "America/Anchorage"
  { id: "US_PACIFIC", value: "US/Pacific", label: "US/Pacific" }, // canonical: "America/Los_Angeles"
  { id: "US_ARIZONA", value: "US/Arizona", label: "US/Arizona" }, // canonical: "America/Phoenix"
  { id: "US_MOUNTAIN", value: "US/Mountain", label: "US/Mountain" }, // canonical: "America/Denver"
  { id: "US_CENTERAL", value: "US/Central", label: "US/Central" }, // canonical: "America/Chicago"
  { id: "US_EASTERN", value: "US/Eastern", label: "US/Eastern" }, // canonical: "America/New_York"
  { id: "CANADA_PACIFIC", value: "Canada/Pacific", label: "Canada/Pacific" }, // canonical: "America/Vancouver"
  { id: "CANADA_MOUNTAIN", value: "Canada/Mountain", label: "Canada/Mountain" }, // canonical: "America/Edmonton"
  { id: "CANADA_CENTERAL", value: "Canada/Central", label: "Canada/Central" }, // canonical: "America/Winnipeg"
  { id: "CANADA_EASTERN", value: "Canada/Eastern", label: "Canada/Eastern" }, // canonical: "America/Toronto"
  { id: "CANADA_ATLANTIC", value: "Canada/Atlantic", label: "Canada/Atlantic" }, // canonical: "America/Halifax"
  {
    id: "MEXICO_BAJA_NORTE",
    value: "Mexico/BajaNorte",
    label: "Mexico/BajaNorte", // canonical: "America/Tijuana"
  },
  { id: "MEXICO_BAJASUR", value: "Mexico/BajaSur", label: "Mexico/BajaSur" }, // canonical: "America/Mazatlan"
  { id: "MEXICO_GENERAL", value: "Mexico/General", label: "Mexico/General" }, // canonical: "America/Mexico_City"
  { id: "EUROPE_ATHENS", value: "Europe/Athens", label: "Europe/Athens" },
  { id: "EUROPE_BERLIN", value: "Europe/Berlin", label: "Europe/Berlin" },
  { id: "EUROPE_DUBLIN", value: "Europe/Dublin", label: "Europe/Dublin" },
  { id: "EUROPE_HELINSKI", value: "Europe/Helsinki", label: "Europe/Helsinki" },
  { id: "EUROPE_KEIV", value: "Europe/Kiev", label: "Europe/Kiev" },
  { id: "EUROPE_LISBON", value: "Europe/Lisbon", label: "Europe/Lisbon" },
  { id: "EUROPE_LONDON", value: "Europe/London", label: "Europe/London" },
  { id: "EUROPE_MADRID", value: "Europe/Madrid", label: "Europe/Madrid" },
  { id: "EUROPE_MOSCOW", value: "Europe/Moscow", label: "Europe/Moscow" },
  { id: "EUROPE_OSLO", value: "Europe/Oslo", label: "Europe/Oslo" },
  { id: "EUROPE_PARIS", value: "Europe/Paris", label: "Europe/Paris" },
  { id: "EUROPE_ROME", value: "Europe/Rome", label: "Europe/Rome" },
  {
    id: "EUROPE_STOCKHOLM",
    value: "Europe/Stockholm",
    label: "Europe/Stockholm",
  },
  { id: "EUROPE_ZURICH", value: "Europe/Zurich", label: "Europe/Zurich" },
  {
    id: "PACIFIC_AUCKLAND",
    value: "Pacific/Auckland",
    label: "Pacific/Auckland",
  },
  { id: "PACIFIC_CHATHAM", value: "Pacific/Chatham", label: "Pacific/Chatham" },
  { id: "GMT", value: "GMT", label: "GMT" },
  { id: "GMT_00_00", value: "GMT+00:00", label: "GMT+00:00" },
  { id: "GMT_01_00", value: "GMT+01:00", label: "GMT+01:00" },
  { id: "GMT_02_00", value: "GMT+02:00", label: "GMT+02:00" },
  { id: "GMT_03_00", value: "GMT+03:00", label: "GMT+03:00" },
  { id: "GMT_04_00", value: "GMT+04:00", label: "GMT+04:00" },
  { id: "GMT_05_00", value: "GMT+05:00", label: "GMT+05:00" },
  { id: "GMT_06_00", value: "GMT+06:00", label: "GMT+06:00" },
  { id: "GMT_07_00", value: "GMT+07:00", label: "GMT+07:00" },
  { id: "GMT_08_00", value: "GMT+08:00", label: "GMT+08:00" },
  { id: "GMT_09_00", value: "GMT+09:00", label: "GMT+09:00" },
  { id: "GMT_10_00", value: "GMT+10:00", label: "GMT+10:00" },
  { id: "GMT_11_00", value: "GMT+11:00", label: "GMT+11:00" },
  { id: "GMT_12_00", value: "GMT+12:00", label: "GMT+12:00" },
  { id: "GMT_13_00", value: "GMT+13:00", label: "GMT+13:00" },
  { id: "GMT_14_00", value: "GMT+14:00", label: "GMT+14:00" },
  { id: "GMT_N_01_00", value: "GMT-01:00", label: "GMT-01:00" },
  { id: "GMT_N_02_00", value: "GMT-02:00", label: "GMT-02:00" },
  { id: "GMT_N_03_00", value: "GMT-03:00", label: "GMT-03:00" },
  { id: "GMT_N_04_00", value: "GMT-04:00", label: "GMT-04:00" },
  { id: "GMT_N_05_00", value: "GMT-05:00", label: "GMT-05:00" },
  { id: "GMT_N_06_00", value: "GMT-06:00", label: "GMT-06:00" },
  { id: "GMT_N_07_00", value: "GMT-07:00", label: "GMT-07:00" },
  { id: "GMT_N_08_00", value: "GMT-08:00", label: "GMT-08:00" },
  { id: "GMT_N_09_00", value: "GMT-09:00", label: "GMT-09:00" },
  { id: "GMT_N_10_00", value: "GMT-10:00", label: "GMT-10:00" },
  { id: "GMT_N_11_00", value: "GMT-11:00", label: "GMT-11:00" },
  { id: "GMT_N_12_00", value: "GMT-12:00", label: "GMT-12:00" },
];

export enum SCHEDULER_PROCESS_STATUS {
  CREATED = 1,
  RUNNING = 2,
  COMPLETED = 3,
  FAILED = 4,
}

export type MODAL_TYPE =
  | "upload"
  | "processing"
  | "longProcess"
  | "success"
  | "error"
  | null;

export enum ASSET_CATEGORY_VALUES {
  ALL = "*",
  ICE = "ICE",
  ELECTRIC = "ELECTRIC",
  HYBRID = "HYBRID",
}

export const ASSET_CATEGORY = [
  { label: "All Vehicles", value: ASSET_CATEGORY_VALUES.ALL },
  { id: "1", label: "ICE", value: ASSET_CATEGORY_VALUES.ICE },
  {
    id: "2",
    label: "Electric",
    value: ASSET_CATEGORY_VALUES.ELECTRIC,
  },
  { id: "3", label: "Hybrid", value: ASSET_CATEGORY_VALUES.HYBRID },
];

export const toTitleCase = (str: string) => {
  return str
    .replace(/_/g, " ") // Replace underscores with spaces
    .toLowerCase() // Convert the string to lowercase
    .replace(/\b\w/g, (match) => match.toUpperCase()); // Capitalize the first letter of each word
};

export const CAM_TYPE = {
  K220: "K220",
  K145C: "K145c",
  K245C: "K245C",
  K165: "K165",
  K265: "K265",
};

export const INFOBOX_DISPLAY_OPTIONS = [
  { fieldName: "infobox_asset_id", label: "Asset ID" },
  { fieldName: "infobox_asset_desc", label: "Asset Description" },
  { fieldName: "infobox_asset_display", label: "Asset Display Name" },
  { fieldName: "infobox_driver", label: "Driver" },
];

export const ACCOUNT_MAP_ICON_SIZE_VALUES = {
  SMALL: 1,
  MEDIUM: 2,
  LARGE: 3,
};

export const ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE =
  ACCOUNT_MAP_ICON_SIZE_VALUES.MEDIUM;

export const ACCOUNT_MAP_ICON_SIZE_OPTIONS = [
  { label: "Small - 1x", value: ACCOUNT_MAP_ICON_SIZE_VALUES.SMALL.toString() },
  {
    label: "Medium - 1.2x (Default)",
    value: ACCOUNT_MAP_ICON_SIZE_VALUES.MEDIUM.toString(),
  },
  {
    label: "Large - 1.5x",
    value: ACCOUNT_MAP_ICON_SIZE_VALUES.LARGE.toString(),
  },
];
